import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { idempotencyKey } from '@/lib/idempotency'
import { formatDate } from '@/lib/format'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { isAmount, parseAmount } from './money'

type Usage = Schema<'UsageResponse'>
type Limit = Schema<'LimitOut'>
type Fx = Schema<'FxOut'>

/**
 * `admin#usage` and `admin#overage`, as one screen.
 *
 * One screen rather than two because they are one state machine: the overage
 * prompt is what the usage screen *becomes* when the ceiling is reached, and
 * routing between them would mean a tenant whose runs have stopped has to find
 * a second page to say so.
 *
 * **The prompt's three choices are the approved ones, in the approved order** —
 * raise the cap for this month, move to a bigger plan, or wait for the period
 * to roll. The design's reasoning is quoted on the card because it is the point
 * of the whole feature: *"the default is to stop, not to keep spending. A
 * surprise bill destroys trust far faster than a paused run."*
 *
 * **Every meter and every row is data.** The plan limits come from
 * `subscriptions.entitlements` and the breakdown from whatever meters have
 * activity, each with the label `app/metering/meters.py` gives it — so the day
 * S-B22 meters DataForSEO or S-B25 meters content, the row appears here with no
 * change to this file. A hardcoded table would be stale the first time it
 * mattered.
 */
export function UsagePage(): JSX.Element {
  const { role, tenant } = useActiveTenant()
  const toast = useToast()
  const manages = may(role, 'budget:manage')
  const [raise, setRaise] = useState('')

  const usage = useQuery((signal) => call(() => api.GET('/api/v1/usage', { signal })), [])

  const grant = useMutation((capMinor: number) =>
    call(() =>
      api.POST('/api/v1/usage/budget/overage', {
        body: { cap_minor: capMinor },
        params: { header: { 'Idempotency-Key': idempotencyKey('overage', tenant.id) } },
      }),
    ),
  )

  if (usage.state.status === 'loading') return <Loading label="Loading usage" lines={5} />
  if (usage.state.status === 'error') {
    return <ErrorState failure={usage.state.failure} onRetry={usage.refetch} />
  }

  const data: Usage = usage.state.data
  const { budget, spend } = data
  // `?? null` throughout: the generated types make every optional field
  // `T | null | undefined`, so a `=== null` check alone leaves `undefined`
  // unnarrowed — the same note `BillingPage` carries about `subscription`.
  const ceiling = budget.effective_display ?? null

  async function onRaise(): Promise<void> {
    const amount = parseAmount(raise)
    if (amount === null) return
    const result = await grant.run(amount)
    if (!result.ok) return
    setRaise('')
    usage.refetch()
    toast.show({ kind: 'success', title: 'Cap raised for this month' })
  }

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Usage this month</h1>
        <Link className="ar-btn ar-btn--ghost ar-btn--sm" to="/settings/budget">
          Budget caps
        </Link>
      </div>

      {spend.paused ? (
        <section className="ar-card">
          <div className="ar-card__body">
            <div className="ar-error-state">
              <span
                className="ar-error-state__icon"
                style={{ background: 'var(--ar-warning-bg)', color: 'var(--ar-warning)' }}
                aria-hidden="true"
              >
                ⚠
              </span>
              <span className="ar-error-state__title">
                You&apos;ve hit your {ceiling ?? 'budget'} cap
              </span>
              <span className="ar-error-state__body">
                Everything is paused — no audits, no content, no outreach. Nothing was left
                half-finished, and nothing has been billed above your cap.
              </span>
            </div>

            <div className="ar-stack" style={{ marginTop: 'var(--ar-space-5)' }}>
              <div className="ar-panel">
                <strong>Raise the cap for this month</strong>
                <p className="ar-muted">
                  One-off. Reverts to {budget.cap_display ?? 'no cap'} on{' '}
                  {formatDate(`${data.resets_on}T00:00:00Z`)}. Billed at cost + 20%.
                </p>
                <InlineError failure={grant.state.failure} />
                <div className="ar-row">
                  <Field label="New cap for this month">
                    {(aria) => (
                      <div className="ar-input-group" style={{ maxWidth: '14rem' }}>
                        <input
                          className="ar-input ar-num"
                          inputMode="decimal"
                          {...aria}
                          disabled={!manages}
                          value={raise}
                          onChange={(event) => {
                            setRaise(event.target.value)
                            grant.reset()
                          }}
                        />
                        <span className="ar-input-group__addon">{budget.currency}</span>
                      </div>
                    )}
                  </Field>
                  <Button
                    tone="primary"
                    disabled={!manages || !isAmount(raise)}
                    busy={grant.state.status === 'running'}
                    onClick={() => {
                      void onRaise()
                    }}
                  >
                    Raise &amp; resume
                  </Button>
                </div>
              </div>

              <div className="ar-panel">
                <strong>Or move to a bigger plan</strong>
                <p className="ar-muted">
                  Worth it if you are raising the cap most months anyway — but it does not raise the
                  cap itself, which stays yours to set.
                </p>
                <Link className="ar-btn ar-btn--ghost" to="/billing/plans">
                  See plans
                </Link>
              </div>

              <div className="ar-panel">
                <strong>Or wait</strong>
                <p className="ar-muted" style={{ margin: 0 }}>
                  Everything resumes automatically on {formatDate(`${data.resets_on}T00:00:00Z`)}.
                  Your rankings and data are untouched in the meantime.
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Plan limits</h3>
          <span className="ar-card__spacer" />
          <span className="ar-subtle">Resets {formatDate(`${data.resets_on}T00:00:00Z`)}</span>
        </div>
        <div className="ar-card__body ar-stack">
          {data.limits.map((limit) => (
            <Meter key={limit.key} limit={limit} />
          ))}

          <hr />

          <div>
            <div className="ar-row" style={{ justifyContent: 'space-between' }}>
              <span>Usage spend against your cap</span>
              <span className="ar-mono">
                {spend.spend_display}
                {ceiling === null ? '' : ` / ${ceiling}`}
              </span>
            </div>
            <Bar percent={spend.percent} />
            <div className="ar-subtle">
              {budget.effective_cap_minor === null
                ? // Not "the four limits": `data.limits` is two counted rows plus
                  // three metered allowances (`app/v1/usage.py:_counted_limits`
                  // and `_metered_limits`), so it renders five, and `PlansPage`
                  // says five. The count is left out rather than corrected to
                  // five, on the same terms as the string below — a number
                  // hard-coded beside a `.map()` drifts the moment the list does.
                  'No cap set. The limits above come with the plan; everything below is metered rather than capped.'
                : 'Your own cap, set in Settings → Budget caps. The limits above come with the plan; this one you choose, and everything below it — rank data, model calls, images, proxy bandwidth — is metered rather than capped by count.'}
            </div>
          </div>
        </div>
      </section>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Where the spend went</h3>
        </div>
        {data.breakdown.length === 0 ? (
          <div className="ar-card__body">
            <Empty
              title="Nothing metered yet"
              body="Rank data, model calls and proxy bandwidth appear here as soon as a run spends anything."
            />
          </div>
        ) : (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th className="ar-num">Units</th>
                  <th className="ar-num">Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.breakdown.map((row) => (
                  <tr key={row.meter}>
                    <td>{row.label}</td>
                    <td className="ar-num">
                      {row.quantity.toLocaleString()} {row.unit}
                      {row.quantity === 1 ? '' : 's'}
                    </td>
                    <td className="ar-num">{row.display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.fx ? <FxNote fx={data.fx} /> : null}
      </section>
    </div>
  )
}

/**
 * Which exchange rate turned dollar vendor invoices into the rupee figures above.
 *
 * Absent for a USD tenant — nothing was converted — and absent when the
 * deployment holds no usable rate, because a caption is not worth failing a
 * screen over. The numbers in the table were converted when they were recorded
 * and do not depend on this line being drawn.
 *
 * It exists because the first question anybody asks about a rupee figure derived
 * from dollar invoices is *at what rate*, and the second is *says who* — and
 * "the rate on the day" is a different claim from "the rate we have configured".
 * The date shown is the day the source **published**, which over a weekend is
 * the Friday rather than today.
 */
function FxNote({ fx }: { fx: Fx }): JSX.Element {
  // `formatDate`, not a second formatter of its own: every other date on this
  // screen goes through it, and two spellings of "24 Jul 2026" on one page is
  // the sort of thing that reads as two different dates.
  const published = formatDate(fx.as_of)
  return (
    <div className="ar-card__footer ar-subtle">
      Vendor costs are billed in US dollars and converted at{' '}
      <span className="ar-mono">{Number(fx.rate).toFixed(2)}</span> per dollar —{' '}
      {fx.source === 'configured'
        ? 'a rate set by AugmenTrait'
        : 'the European Central Bank’s published rate'}{' '}
      for {published}.
    </div>
  )
}

/** One plan allowance, with the meter S-D3 draws beside it. */
function Meter({ limit }: { limit: Limit }): JSX.Element {
  // `?? null` for the same reason the page does it: an optional field in the
  // generated types is `number | null | undefined`, and only one of those two
  // absences narrows away on a `=== null`.
  const allowance = limit.limit ?? null
  return (
    <div>
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <span>
          {limit.label}
          {limit.window === 'week' ? <span className="ar-subtle"> · this week</span> : null}
        </span>
        <span className="ar-mono">
          {limit.used.toLocaleString()}
          {allowance === null ? '' : ` / ${allowance.toLocaleString()}`}
        </span>
      </div>
      <Bar percent={limit.percent} />
    </div>
  )
}

/**
 * The meter bar. Absent rather than empty when there is no limit: a bar at 0%
 * would say "you have used none of your allowance" where the truth is that no
 * plan states one.
 */
function Bar({ percent }: { percent: number | null | undefined }): JSX.Element | null {
  if (percent === null || percent === undefined) return null
  const width = Math.min(100, Math.max(0, percent))
  return (
    <div
      className="ar-meter"
      role="meter"
      aria-valuenow={width}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="ar-meter__fill" style={{ width: `${String(width)}%` }} />
    </div>
  )
}
